import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "./config";

const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchConversations = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const res = await axios.get(`${API_BASE_URL}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setConversations(res.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching conversations:", err);
      setError("Failed to load conversations");
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchConversations();
    // Refresh conversations every 30 seconds
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return "Yesterday";
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const truncateMessage = (message, maxLength = 50) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + "...";
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <h4 className="mb-0">Messages</h4>
            </div>
            <div className="card-body p-0">
              {error && (
                <div className="alert alert-danger m-3">{error}</div>
              )}

              {conversations.length === 0 ? (
                <div className="text-center p-5">
                  <p className="text-muted">No conversations yet</p>
                  <p className="text-muted small">
                    Start messaging by visiting a user's profile who follows you back
                  </p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {conversations.map((conv) => (
                    <Link
                      key={conv.conversationId}
                      to={`/messages/${conv.otherUser._id}`}
                      className="list-group-item list-group-item-action"
                      style={{ textDecoration: 'none' }}
                    >
                      <div className="d-flex align-items-center">
                        <img
                          src={conv.otherUser.profilePic ? 
                            (conv.otherUser.profilePic.startsWith('http') 
                              ? conv.otherUser.profilePic 
                              : `${API_BASE_URL}${conv.otherUser.profilePic.startsWith('/') ? '' : '/'}${conv.otherUser.profilePic}`)
                            : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              conv.otherUser.displayUsername || conv.otherUser.username || 'User'
                            )}&background=007bff&color=fff&size=50`}
                          alt={conv.otherUser.displayUsername || conv.otherUser.username}
                          className="rounded-circle me-3"
                          style={{ width: "50px", height: "50px", objectFit: "cover" }}
                          onError={(e) => {
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              conv.otherUser.displayUsername || conv.otherUser.username || 'User'
                            )}&background=007bff&color=fff&size=50`;
                          }}
                        />
                        
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h6 className="mb-1">
                                {conv.otherUser.displayUsername || conv.otherUser.username}
                                {conv.unreadCount > 0 && (
                                  <span className="badge bg-primary ms-2">
                                    {conv.unreadCount}
                                  </span>
                                )}
                              </h6>
                              <p className="mb-0 text-muted small">
                                {truncateMessage(conv.lastMessage.content)}
                              </p>
                            </div>
                            <small className="text-muted">
                              {formatTime(conv.lastMessage.createdAt)}
                            </small>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;